import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';
import { imageOptimizer } from '../utils/imageOptimizer';

/**
 * Firebase Storageを使用した画像管理サービス
 */
export const storageService = {
  /**
   * 画像をアップロードする
   * @param {File} file - アップロードするファイル
   * @param {string} path - ストレージ内のパス
   * @param {Object} currentUser - 現在のユーザー情報（独自認証用）
   * @returns {Promise<Object>} アップロード結果
   */
  uploadImage: async (file, path = 'quiz-images', currentUser = null) => {
    try {
      // 独自認証システムでの権限チェック
      if (!currentUser || currentUser.role !== 'teacher') {
        throw new Error('画像のアップロードには教師権限が必要です。');
      }

      // 画像圧縮・最適化を実行
      const optimizedFile = await imageOptimizer.compressImage(file, {
        maxWidth: 800,
        maxHeight: 600,
        quality: 0.8,
        format: 'webp'
      });

      // ファイル名を生成（タイムスタンプ + 最適化ファイル名）
      const timestamp = Date.now();
      const filename = `${timestamp}_${optimizedFile.name}`;
      const fullPath = `${path}/${filename}`;

      // ストレージ参照を作成
      const storageRef = ref(storage, fullPath);

      // 最適化されたファイルをアップロード
      const snapshot = await uploadBytes(storageRef, optimizedFile);

      // ダウンロードURLを取得
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        filename: filename,
        fullPath: fullPath,
        originalSize: file.size,
        optimizedSize: optimizedFile.size,
        compressionRatio: ((file.size - optimizedFile.size) / file.size * 100).toFixed(1),
        type: optimizedFile.type,
        uploadedAt: timestamp,
        uploadedBy: currentUser.id
      };
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error);
      throw new Error(`画像のアップロードに失敗しました: ${error.message}`);
    }
  },

  /**
   * 画像を削除する
   * @param {string} fullPath - ストレージ内のフルパス
   * @param {Object} currentUser - 現在のユーザー情報（独自認証用）
   * @returns {Promise<void>}
   */
  deleteImage: async (fullPath, currentUser = null) => {
    try {
      // 独自認証システムでの権限チェック
      if (!currentUser || currentUser.role !== 'teacher') {
        throw new Error('画像の削除には教師権限が必要です。');
      }

      const storageRef = ref(storage, fullPath);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('画像の削除に失敗しました:', error);
      throw new Error(`画像の削除に失敗しました: ${error.message}`);
    }
  },

  /**
   * 画像ファイルのバリデーション
   * @param {File} file - 検証するファイル
   * @returns {Object} バリデーション結果
   */
  validateImage: (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    const errors = [];

    if (!file) {
      errors.push('ファイルが選択されていません。');
    } else {
      if (file.size > maxSize) {
        errors.push('ファイルサイズは5MB以下にしてください。');
      }

      if (!allowedTypes.includes(file.type)) {
        errors.push('対応しているファイル形式: JPEG, PNG, GIF, WebP');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}; 